import { aabb, descendantsOf, SceneStore, topLevel } from '@os/doc';
import { chooseVariant, type Variant } from '../editor/lod';

export type Rect = { x: number; y: number; w: number; h: number };
export type CanvasLimits = { side: number; area: number };
export type ExportTarget = { kind: 'board' } | { kind: 'selection'; ids: readonly string[] } | { kind: 'section'; id: string };

const MAX_SIDE = 16384;
const PDF_MAX = 14400;

/** Canvas limits of the browser (spec §12, plan P9 Ruling 7): iOS and iPadOS Safari cap the surface at ~16.7 Mpx. */
export function canvasLimits(nav: { platform?: string; userAgent?: string; maxTouchPoints?: number } = globalThis.navigator ?? {}): CanvasLimits {
  const ios = /iPad|iPhone|iPod/.test(`${nav.platform ?? ''} ${nav.userAgent ?? ''}`) || (nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1);
  return { side: MAX_SIDE, area: ios ? 16_777_216 : 268_435_456 };
}

/** Output size within the limits (the scale is lowered when needed, `reduced`), cut in tiles of `tile` output px. */
export function planRaster(size: { w: number; h: number }, scale: number, limits: CanvasLimits, tile = 4096) {
  const fits = (s: number) => {
    const [w, h] = [Math.ceil(size.w * s), Math.ceil(size.h * s)];
    return w <= limits.side && h <= limits.side && w * h <= limits.area;
  };
  let s = scale;
  if (!fits(s)) {
    s = Math.floor(Math.min(scale, limits.side / size.w, limits.side / size.h, Math.sqrt(limits.area / (size.w * size.h))) * 100) / 100;
    while (s > 0.01 && !fits(s)) s = Math.round((s - 0.01) * 100) / 100;
  }
  const [width, height] = [Math.ceil(size.w * s), Math.ceil(size.h * s)];
  const tiles: Rect[] = [];
  for (let y = 0; y < height; y += tile) for (let x = 0; x < width; x += tile) tiles.push({ x, y, w: Math.min(tile, width - x), h: Math.min(tile, height - y) });
  return { scale: s, reduced: s < scale, width, height, tiles };
}

/** A PDF page of the frame's size, proportionally reduced to 14 400 units per side at most. */
export function pdfPage(size: { w: number; h: number }): { w: number; h: number; orientation: 'p' | 'l' } {
  const k = Math.min(1, PDF_MAX / Math.max(size.w, size.h));
  return { w: size.w * k, h: size.h * k, orientation: size.w >= size.h ? 'l' : 'p' };
}

const union = (rects: { minX: number; minY: number; maxX: number; maxY: number }[], pad: number): Rect | null => {
  if (rects.length === 0) return null;
  const minX = Math.min(...rects.map((r) => r.minX)) - pad;
  const minY = Math.min(...rects.map((r) => r.minY)) - pad;
  return { x: minX, y: minY, w: Math.max(...rects.map((r) => r.maxX)) + pad - minX, h: Math.max(...rects.map((r) => r.maxY)) + pad - minY };
};

/**
 * What an export draws: the board's or the selection's top-level objects with a margin, or the world clipped to a
 * section's rectangle (every top-level object that crosses it, plan P9 Ruling 5). Null when there is nothing.
 */
export function resolveTarget(store: SceneStore, target: ExportTarget, pad = 32): { rect: Rect; roots: string[] } | null {
  const g = store.graph();
  const box = (id: string) => {
    const w = g.world.get(id);
    return w && !g.hidden.has(id) ? aabb(w) : null;
  };
  const extent = (roots: string[]) => roots.flatMap((id) => [id, ...descendantsOf(g, id)]).flatMap((id) => box(id) ?? []);
  if (target.kind === 'section') {
    const w = g.world.get(target.id);
    if (!w || g.hidden.has(target.id) || store.getObject(target.id)?.type !== 'section') return null;
    const rect = { x: w.x, y: w.y, w: w.w, h: w.h };
    const roots = store
      .list()
      .filter((o) => !g.parent.get(o.id) && !g.hidden.has(o.id))
      .map((o) => o.id)
      .filter((id) => extent([id]).some((b) => b.minX < rect.x + rect.w && b.maxX > rect.x && b.minY < rect.y + rect.h && b.maxY > rect.y));
    return { rect, roots };
  }
  const roots =
    target.kind === 'board'
      ? store.list().filter((o) => !g.parent.get(o.id) && !g.hidden.has(o.id)).map((o) => o.id)
      : topLevel(g, [...target.ids]).filter((id) => store.getObject(id) && !g.hidden.has(id));
  const rect = union(extent(roots), pad);
  return rect && roots.length > 0 ? { rect, roots } : null;
}

/** Visible sections of a page (the active one by default), top to bottom then left to right (the minimap's list and the PDF's pages). */
export function sectionsInReadingOrder(store: SceneStore, pageId: string = store.page): { id: string; name: string; rect: Rect }[] {
  const g = store.graphOf(pageId);
  return store
    .listPage(pageId)
    .flatMap((o) => {
      const w = g.world.get(o.id);
      return o.type === 'section' && w && !g.hidden.has(o.id) ? [{ id: o.id, name: o.name, rect: { x: w.x, y: w.y, w: w.w, h: w.h } }] : [];
    })
    .sort((a, b) => a.rect.y - b.rect.y || a.rect.x - b.rect.x);
}

/** Per asset, the smallest variant covering its largest drawing at `scale`, never above `cap` (plan P9 Ruling 6). */
export function exportVariants(store: SceneStore, roots: readonly string[], scale: number, cap: Variant): Map<string, Variant> {
  const g = store.graph();
  const need = new Map<string, number>();
  const add = (id: string | null, px: number) => id && need.set(id, Math.max(need.get(id) ?? 0, px));
  for (const id of roots.flatMap((r) => [r, ...descendantsOf(g, r)])) {
    const o = store.getObject(id);
    if (o?.type === 'image') add(o.assetId, Math.max(o.w / (o.crop.w || 1), o.h / (o.crop.h || 1)));
    else if (o?.type === 'link' || o?.type === 'embed') add(o.thumbAssetId, Math.max(o.w, o.h));
  }
  return new Map([...need].map(([id, px]) => [id, chooseVariant(px * scale, 1, cap)]));
}

/** One page of the PDF: a target drawn from `view`, a read-only store on the target's page (release it when done). */
export type PdfEntry = { pageId: string; view: SceneStore; rect: Rect; roots: string[]; release(): void };

/**
 * The PDF's pages (P14 Ruling 17): every board page in order, each as its sections in reading order, a page without
 * sections as its whole content, an empty page left out. Other pages are drawn from a second store on the same
 * document (the open page never changes), released by the caller.
 */
export function pdfPlan(store: SceneStore): PdfEntry[] {
  const out: PdfEntry[] = [];
  for (const page of store.pages()) {
    const mine = page.id === store.page;
    const view = mine ? store : new SceneStore(store.doc);
    if (!mine) {
      view.setReadOnly(true);
      view.setPage(page.id);
    }
    const sections = sectionsInReadingOrder(view);
    const targets = (sections.length > 0 ? sections.map((s) => resolveTarget(view, { kind: 'section', id: s.id })) : [resolveTarget(view, { kind: 'board' })]).flatMap((t) => t ?? []);
    if (targets.length === 0 && !mine) view.destroy();
    targets.forEach((t, i) => {
      const last = i === targets.length - 1;
      out.push({ pageId: page.id, view, rect: t.rect, roots: t.roots, release: () => void (!mine && last && view.destroy()) });
    });
  }
  return out;
}
